// Ninocat Link — download the paired port-forwarding exe, see its status,
// revoke it. Server side: internal/api/link.go.
import { createResource, Show } from 'solid-js';
import Modal from './Modal.jsx';
import { toast } from '../lib/toast.js';
import { linkStatus, revokeLink, linkDownloadUrl } from '../lib/link.js';

const when = (secs) => (secs ? new Date(secs * 1000).toLocaleString() : '—');

export default function NinocatLink(props) {
  const [st, { refetch }] = createResource(linkStatus);

  const revoke = async () => {
    if (!confirm('Revoke your Ninocat Link? Every downloaded copy stops working and open sessions are cut.')) return;
    try { await revokeLink(); toast.success('Ninocat Link revoked.'); refetch(); }
    catch (e) { toast.error('Revoke failed: ' + e.message); }
  };
  // A new download replaces the token, so give the status a moment, then refresh.
  const downloaded = () => setTimeout(refetch, 1500);

  return (
    <Modal open={true} onClose={props.onClose} title="Ninocat Link" wide>
      <p class="muted" style="font-size:12px;margin:0 0 12px;">
        Use your Ninocat connections from apps on your PC (ERP, HeidiSQL, Navicat …). Ninocat Link opens a
        local port for each connection you can use — e.g. <code>127.0.0.1:13001</code> — and carries the
        traffic through Ninocat to the database, wherever it is (connector, SSH bastion or direct). Point the
        app at that port and log in with its <strong>own MySQL username and password</strong>.
      </p>
      <Show when={!st.loading} fallback={<div class="muted">Loading…</div>}>
        <Show when={st()?.allowed} fallback={
          <div class="muted" style="font-size:12px;">Port forwarding isn't enabled for your account — ask an admin (Users → Port forwarding).</div>
        }>
          <div class="settings-row"><label>Status</label>
            <span>
              {st()?.hasToken ? `Downloaded ${when(st().createdAt)}` : 'Not downloaded yet'}
              {st()?.hasToken && st()?.lastUsedAt ? ` · last used ${when(st().lastUsedAt)}` : ''}
              {st()?.sessions ? ` · ${st().sessions} running now` : ''}
            </span>
          </div>
          <div style="display:flex;gap:8px;flex-wrap:wrap;align-items:center;margin:10px 0;">
            <a class="btn" href={linkDownloadUrl('amd64')} onClick={downloaded}>⬇ Download for Windows (64-bit)</a>
            <a class="btn btn-ghost" href={linkDownloadUrl('386')} onClick={downloaded}>⬇ Windows (32-bit)</a>
            <span class="spacer" />
            <Show when={st()?.hasToken}>
              <button class="btn btn-ghost btn-danger" onClick={revoke}>Revoke</button>
            </Show>
          </div>
          <ul class="muted" style="font-size:12px;margin:6px 0 0;padding-left:18px;line-height:1.6;">
            <li>Run the downloaded <code>.exe</code> and keep its window open; it lists your ports. Works on Windows 7 and later.</li>
            <li>Each connection keeps the same port on a PC (by default 13000 + its id; if that port is taken, a free one is chosen and remembered on that PC).</li>
            <li>The download is paired to <strong>you</strong>. Downloading again replaces the previous copy (older copies stop working). Don't share it.</li>
            <li>Only <code>127.0.0.1</code> is used — other computers on the network can't reach your ports.</li>
          </ul>
        </Show>
      </Show>
    </Modal>
  );
}
